import { useState } from "react";
import { toast } from "sonner";
import { Mail, Send, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";

const TEMPLATES = [
  { id: "blank",     label: "Custom message", subject: "",                                          body: "" },
  { id: "reminder",  label: "Payment reminder",
    subject: "Reminder: please upload your ClassWallet invoice",
    body: "Hi {parent},\n\nJust checking in — we haven't yet seen your ClassWallet submission for {student}'s seat in {class}. The PDF invoice is still linked in your confirmation email if you need it again.\n\nLet me know if you need anything!\n\n— Heather" },
  { id: "followup",  label: "Post-class follow-up",
    subject: "How was {class}?",
    body: "Hi {parent},\n\nThanks for joining us at {class}! I'd love to hear how {student} liked it — and if there's a particular skill they'd like to dig into next, I can recommend the right upcoming class or 1-on-1.\n\nFeel free to reply with photos or questions.\n\n— Heather" },
];

function fillTemplate(text, reg) {
  return (text || "")
    .replace(/\{parent\}/g, reg.parent_name || "")
    .replace(/\{student\}/g, reg.student_name || "")
    .replace(/\{class\}/g, reg.class_title || "the class")
    .replace(/\{invoice\}/g, `#${reg.invoice_number || ""}`);
}

export default function FollowUpDialog({ reg, onClose }) {
  const [templateId, setTemplateId] = useState("blank");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [includeInvoice, setIncludeInvoice] = useState(false);
  const [busy, setBusy] = useState(false);

  const applyTemplate = (id) => {
    setTemplateId(id);
    const t = TEMPLATES.find((x) => x.id === id);
    if (!t) return;
    setSubject(fillTemplate(t.subject, reg));
    setMessage(fillTemplate(t.body, reg));
    if (id === "reminder") setIncludeInvoice(true);
  };

  const send = async () => {
    if (!subject.trim() || !message.trim()) {
      toast.error("Subject and message are required");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/registrations/${reg.id}/follow-up`, {
        subject,
        message,
        include_invoice_link: includeInvoice,
      });
      toast.success(`Sent to ${reg.parent_email}`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-gg-surface max-h-[90vh] overflow-y-auto" data-testid="follow-up-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl inline-flex items-center gap-2">
            <Mail size={20} /> Send follow-up to {reg.parent_name}
          </DialogTitle>
          <p className="text-sm text-gg-muted">{reg.parent_email}</p>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <p className="text-xs uppercase tracking-[0.16em] text-gg-brown font-semibold mb-2">Quick templates</p>
            <div className="flex flex-wrap gap-1">
              {TEMPLATES.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => applyTemplate(t.id)}
                  data-testid={`followup-template-${t.id}`}
                  className={`text-xs px-3 py-1.5 rounded-full transition-colors ${
                    templateId === t.id ? "bg-gg-green text-white" : "bg-gg-bg border border-gg-border text-gg-brown hover:border-gg-green"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-gg-muted italic mt-2">
              Tip: use <code>{`{parent}`}</code>, <code>{`{student}`}</code>, <code>{`{class}`}</code>, <code>{`{invoice}`}</code> in your message — they'll auto-fill.
            </p>
          </div>

          <label className="block">
            <span className="text-xs uppercase tracking-[0.16em] text-gg-brown font-semibold">Subject</span>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1.5" data-testid="followup-subject" />
          </label>

          <label className="block">
            <span className="text-xs uppercase tracking-[0.16em] text-gg-brown font-semibold">Message</span>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={9}
              className="mt-1.5 font-mono text-sm"
              placeholder="Write your message — blank lines become paragraphs."
              data-testid="followup-message"
            />
          </label>

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={includeInvoice}
              onChange={(e) => setIncludeInvoice(e.target.checked)}
              data-testid="followup-include-invoice"
            />
            <span>Include "Open invoice" button in the email</span>
          </label>
        </div>

        <DialogFooter className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-pill btn-outline">
            <X size={14} /> Cancel
          </button>
          <button type="button" onClick={send} disabled={busy} className="btn-pill btn-primary disabled:opacity-60" data-testid="followup-send">
            <Send size={14} /> {busy ? "Sending…" : `Send to ${reg.parent_email.split("@")[0]}…`}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
